import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';
import type { ApiError } from '@app/api/ApiError';
import { del, get, post, put, withPath, type ApiPath } from '@app/api/typed';
import type {
  ChatDefaultAgent,
  ChatEntryState,
  ChatEntryTestRequest,
  ChatEntryTestResult,
  ChatEntryUpdate,
} from './ChatEntryModel';

/**
 * Hooks of the chat entry point (aim-5wc). The PUT and the DELETE answer the new state, and it goes straight into the
 * cache of that identity: what the line «Entrada» shows is what was saved. Both invalidate the default agent too —
 * the PUT carries the mark of D8.
 */
export const chatEntryKeys = {
  all: ['chat-entry'] as const,
  of: (identityId: string) => ['chat-entry', identityId] as const,
  defaultAgent: () => ['chat-entry', 'default-agent'] as const,
};

function route<P extends ApiPath>(template: P, id: string): P {
  return withPath(template, { id }) as P;
}

export function useChatEntry(identityId: string | null): UseQueryResult<ChatEntryState, ApiError> {
  return useQuery<ChatEntryState, ApiError>({
    queryKey: chatEntryKeys.of(identityId ?? ''),
    queryFn: () => get(route('/api/v1/identities/{id}/chat-entry', identityId ?? '')),
    enabled: Boolean(identityId),
  });
}

export function useChatDefaultAgent(): UseQueryResult<ChatDefaultAgent, ApiError> {
  return useQuery<ChatDefaultAgent, ApiError>({
    queryKey: chatEntryKeys.defaultAgent(),
    queryFn: () => get('/api/v1/agents/chat-default'),
  });
}

export interface UpdateChatEntryVars {
  identityId: string;
  body: ChatEntryUpdate;
}

export function useUpdateChatEntry(): UseMutationResult<
  ChatEntryState,
  ApiError,
  UpdateChatEntryVars
> {
  const queryClient = useQueryClient();
  return useMutation<ChatEntryState, ApiError, UpdateChatEntryVars>({
    mutationFn: ({ identityId, body }) =>
      put(route('/api/v1/identities/{id}/chat-entry', identityId), { body }),
    onSuccess: (state, { identityId }) => {
      queryClient.setQueryData(chatEntryKeys.of(identityId), state);
      // Marking one agent unmarks the previous: every other state in the cache may now say the wrong thing.
      void queryClient.invalidateQueries({ queryKey: chatEntryKeys.all });
    },
  });
}

export function useDeleteChatEntry(): UseMutationResult<ChatEntryState, ApiError, string> {
  const queryClient = useQueryClient();
  return useMutation<ChatEntryState, ApiError, string>({
    mutationFn: (identityId) => del(route('/api/v1/identities/{id}/chat-entry', identityId)),
    onSuccess: (state, identityId) => {
      queryClient.setQueryData(chatEntryKeys.of(identityId), state);
      void queryClient.invalidateQueries({ queryKey: chatEntryKeys.defaultAgent() });
    },
  });
}

export interface TestChatEntryVars {
  identityId: string;
  body: ChatEntryTestRequest;
}

/** The test is a POST and has an effect on nobody — a mutation all the same: it must never re-run by itself. */
export function useTestChatEntry(): UseMutationResult<
  ChatEntryTestResult,
  ApiError,
  TestChatEntryVars
> {
  return useMutation<ChatEntryTestResult, ApiError, TestChatEntryVars>({
    mutationFn: ({ identityId, body }) =>
      post(route('/api/v1/identities/{id}/chat-entry/test', identityId), { body }),
  });
}
